"use client"

import Link from "next/link"
import { cn } from "@/lib/utils"
import { ArrowUpRight, Lightbulb, Rocket, Cpu } from "lucide-react"
import { useTranslations } from "next-intl"

const colorMap = {
  amber: {
    accent: "text-amber-500",
    border: "border-amber-500/20",
    bg: "bg-amber-500/5",
    hover: "group-hover:border-amber-500/40"
  },
  teal: {
    accent: "text-teal-500",
    border: "border-teal-500/20",
    bg: "bg-teal-500/5",
    hover: "group-hover:border-teal-500/40"
  },
  slate: {
    accent: "text-slate-400",
    border: "border-slate-400/20",
    bg: "bg-slate-400/5",
    hover: "group-hover:border-slate-400/40"
  }
}

// Entity data with translations keys
const entitiesData = [
  {
    key: "holistika",
    icon: Lightbulb,
    color: "amber" as const,
    link: "/manifiesto"
  },
  {
    key: "thinkBig",
    icon: Rocket,
    color: "teal" as const,
    link: "/services"
  },
  {
    key: "techLab",
    icon: Cpu,
    color: "slate" as const,
    link: "/tech-lab"
  }
]

export function EntitiesSection() {
  const t = useTranslations("entities")
  
  return (
    <section className="py-24 relative">
      {/* Section header */}
      <div className="text-center mb-16">
        <p className="text-accent-primary text-sm font-medium uppercase tracking-widest mb-3">
          {t("sectionLabel")}
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
          {t("title")}
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          {t("subtitle")}
        </p>
      </div>

      {/* Entities grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {entitiesData.map((entity) => {
          const Icon = entity.icon
          const colors = colorMap[entity.color]
          
          return (
            <Link
              key={entity.key}
              href={entity.link}
              className={cn(
                "group relative rounded-2xl border p-8 transition-all duration-300",
                colors.border,
                colors.hover,
                "hover:bg-card/50"
              )}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className={cn(
                  "inline-flex items-center justify-center w-14 h-14 rounded-xl",
                  colors.bg,
                  colors.border,
                  "border"
                )}>
                  <Icon className={cn("w-7 h-7", colors.accent)} />
                </div>
                <ArrowUpRight className="w-5 h-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>

              {/* Content */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-2xl font-bold text-foreground">
                    {t(`${entity.key}.name`)}
                  </h3>
                  <p className={cn("text-sm font-medium", colors.accent)}>
                    {t(`${entity.key}.tagline`)}
                  </p>
                </div>

                <p className="text-muted-foreground leading-relaxed">
                  {t(`${entity.key}.description`)}
                </p>
              </div>

              {/* Bottom border accent */}
              <div className={cn(
                "absolute bottom-0 left-8 right-8 h-px transition-all duration-300",
                "bg-gradient-to-r from-transparent via-current to-transparent opacity-0 group-hover:opacity-100",
                colors.accent
              )} />
            </Link>
          )
        })}
      </div>

      {/* Connection line visual */}
      <div className="hidden lg:flex justify-center items-center mt-12">
        <div className="flex items-center gap-4">
          <div className="w-16 h-px bg-gradient-to-r from-amber-500/50 to-amber-500/20" />
          <div className="w-2 h-2 rounded-full bg-amber-500/50" />
          <div className="w-24 h-px bg-gradient-to-r from-amber-500/20 via-teal-500/40 to-teal-500/20" />
          <div className="w-2 h-2 rounded-full bg-teal-500/50" />
          <div className="w-24 h-px bg-gradient-to-r from-teal-500/20 via-slate-400/40 to-slate-400/20" />
          <div className="w-2 h-2 rounded-full bg-slate-400/50" />
          <div className="w-16 h-px bg-gradient-to-l from-slate-400/50 to-slate-400/20" />
        </div>
      </div>
      <p className="text-center text-muted-foreground/60 text-sm mt-4">
        {t("connectionLine")}
      </p>
    </section>
  )
}
